import { ChevronRightIcon, Loader2Icon, XIcon, type LucideIcon } from "lucide-react"
import { useEffect, useId, useRef, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/primitives/dialog.tsx"
import { Input } from "@/primitives/input.tsx"
import { Switch } from "@/primitives/switch.tsx"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { IdentityTile } from "./identity.tsx"
import { Kbd } from "./key.tsx"

/*
 * The dialog every new thing is made in: a ticket, an Epic, a Project. It opens high on the page,
 * says where the new thing goes (the Project's tile and key, then what is being made), and is mostly
 * its title, typed large, with the focus already in it. Beneath the title come the page's own
 * fields (`body`, such as a bare MarkdownEditor for the description), then its property chips
 * (`chips`, PropertyChips opening Pickers).
 *
 * ⌘↵ submits from anywhere in it, once `canSubmit`. With `createMore`, the page keeps it open after
 * a submit and clears the title, and the focus goes back to the title for the next one. A refusal
 * about the title shows under it (`titleError`); the page toasts any other.
 */
export function Composer({
  open,
  onOpenChange,
  crumb,
  titlePlaceholder,
  title,
  onTitle,
  titleError,
  body,
  chips,
  footerStart,
  submitLabel,
  onSubmit,
  pending,
  canSubmit,
  createMore,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Where the new thing goes: a Project's key, or an icon for something made outside one. With
   * both, the icon follows the key and says what kind of thing is made in it, as an Epic's bolt.
   */
  crumb: { projectKey?: string; icon?: LucideIcon; label: string }
  titlePlaceholder: string
  title: string
  onTitle: (title: string) => void
  titleError?: string
  body?: ReactNode
  chips?: ReactNode
  /** A line on what happens next, such as where a new ticket lands. */
  footerStart?: string
  submitLabel: string
  onSubmit: () => void
  pending: boolean
  canSubmit: boolean
  createMore?: { checked: boolean; onChange: (checked: boolean) => void }
}) {
  const popup = useRef<HTMLDivElement>(null)
  const titleField = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const submittable = canSubmit && !pending

  useShortcut(
    "submit",
    () => {
      if (submittable) onSubmit()
    },
    { within: popup, enabled: open },
  )

  // After a submit that keeps the composer open for the next one, typing starts at the title.
  const wasPending = useRef(pending)
  useEffect(() => {
    if (wasPending.current && !pending && open && title === "") titleField.current?.focus()
    wasPending.current = pending
  }, [pending, open, title])

  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DialogContent
        ref={popup}
        position="top"
        size="lg"
        showCloseButton={false}
        initialFocus={titleField}
      >
        <div className="flex h-11 items-center gap-2 px-4">
          <DialogTitle className="flex min-w-0 items-center gap-1.5 text-xs font-normal text-muted-foreground">
            {crumb.projectKey !== undefined && (
              <>
                <IdentityTile projectKey={crumb.projectKey} size={16} />
                <span className="font-medium">{crumb.projectKey}</span>
                <ChevronRightIcon aria-hidden className="size-3 text-subtle-foreground" />
              </>
            )}
            {/* After a Project's key, an icon says what kind of thing is made in it: an Epic's bolt. */}
            {crumb.icon && <crumb.icon aria-hidden className="size-3.5" />}
            <span className="truncate">{crumb.label}</span>
          </DialogTitle>
          <DialogClose
            render={
              <Button variant="ghost" size="icon-sm" className="ml-auto" aria-label="Close" />
            }
          >
            <XIcon aria-hidden />
          </DialogClose>
        </div>

        <div className="space-y-2 px-5 pb-2">
          <div className="space-y-1">
            <Input
              ref={titleField}
              variant="bare"
              aria-label={titlePlaceholder}
              aria-invalid={titleError ? true : undefined}
              aria-describedby={titleError ? errorId : undefined}
              placeholder={titlePlaceholder}
              value={title}
              onChange={(event) => onTitle(event.target.value)}
              className="text-heading font-semibold"
            />
            {titleError && (
              <p id={errorId} className="text-xs text-destructive">
                {titleError}
              </p>
            )}
          </div>
          {body && <div className="min-h-28 text-prose">{body}</div>}
          {chips && <div className="flex flex-wrap gap-1.5 pt-2">{chips}</div>}
        </div>

        <div className="flex h-12 items-center gap-2 border-t border-border-subtle px-4">
          {footerStart && (
            <p className="line-clamp-2 min-w-0 text-xs leading-4 text-muted-foreground">
              {footerStart}
            </p>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {createMore && (
              <label className="mr-1 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={createMore.checked} onCheckedChange={createMore.onChange} />
                Create more
              </label>
            )}
            <DialogClose render={<Button variant="ghost" size="sm" />}>Cancel</DialogClose>
            <Button
              size="sm"
              disabled={!submittable}
              // Submitting, it keeps its colour and takes no second press.
              focusableWhenDisabled={pending}
              onClick={onSubmit}
            >
              {pending && <Loader2Icon aria-hidden className="animate-spin" />}
              {submitLabel}
              {!pending && <Kbd shortcut="submit" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
